import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import Alert from '../../../shared/components/Alert/Alert';
import Box from '../../../shared/components/Box/Box';
import Button from '../../../shared/components/Button/Button';
import Input from '../../../shared/components/Input/Input';
import Select from '../../../shared/components/Select/Select';
import Typography from '../../../shared/components/Typography/Typography';
import { createUser, getUsers, updateUser } from './userSettings.api';

const ROLE_OPTIONS = [
  { value: 'employee', label: 'Collaborateur' }, { value: 'manager', label: 'Manager' },
  { value: 'hr', label: 'RH' }, { value: 'admin', label: 'Administrateur' },
];
const QUOTE_PERMISSION_GROUPS = [
  { title: 'Accès', permissions: [['quote.read', 'Consulter les devis'], ['quote.create', 'Créer des devis'], ['quote.edit_draft', 'Modifier les brouillons']] },
  { title: 'Chiffrage', permissions: [['quote.force_price', 'Forcer les prix'], ['quote.force_coefficient', 'Forcer les coefficients'], ['quote.view_minute', 'Consulter la minute'], ['quote.view_costs', 'Consulter les coûts']] },
  { title: 'Cycle de vie', permissions: [['quote.validate', 'Valider'], ['quote.reopen_validated', 'Rouvrir un devis validé'], ['quote.issue', 'Émettre'], ['quote.accept', 'Accepter'], ['quote.refuse', 'Refuser'], ['quote.archive', 'Archiver']] },
];
const ALL_PERMISSIONS = QUOTE_PERMISSION_GROUPS.flatMap((group) => group.permissions.map(([value]) => value));
const EMPLOYEE_PERMISSIONS = ALL_PERMISSIONS.filter((value) => ['quote.read', 'quote.create', 'quote.edit_draft', 'quote.force_price', 'quote.force_coefficient', 'quote.view_minute', 'quote.view_costs'].includes(value));
const permissionsForRole = (role) => ['admin', 'manager'].includes(role) ? ALL_PERMISSIONS : EMPLOYEE_PERMISSIONS;
const initialForm = { firstName: '', lastName: '', email: '', phone: '', password: '', role: 'employee', status: 'active', isActive: true };
const isAdmin = () => {
  try {
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    return Number(user.roleId) === 1 || String(user.role || '').toLowerCase() === 'admin';
  } catch { return false; }
};

export default function UserSettings() {
  const [form, setForm] = useState(initialForm);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [editedRole, setEditedRole] = useState('employee');
  const [permissions, setPermissions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingRights, setSavingRights] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const selectedUser = useMemo(() => users.find((user) => String(user.id) === selectedUserId), [users, selectedUserId]);

  useEffect(() => {
    if (!isAdmin()) return;
    getUsers().then(setUsers).catch((requestError) => setError(requestError.message || 'Impossible de charger les utilisateurs.')).finally(() => setLoadingUsers(false));
  }, []);
  if (!isAdmin()) return <Navigate to="/dashboard" replace />;

  const selectUser = (event) => {
    const id = event.target.value;
    const user = users.find((item) => String(item.id) === id);
    const role = user?.role || 'employee';
    setSelectedUserId(id); setEditedRole(role);
    setPermissions(Array.isArray(user?.quotePermissions) ? user.quotePermissions : permissionsForRole(role));
    setError(''); setNotice('');
  };
  const changeRole = (event) => {
    const role = event.target.value;
    setEditedRole(role); setPermissions(permissionsForRole(role));
  };
  const togglePermission = (permission) => setPermissions((current) => current.includes(permission) ? current.filter((item) => item !== permission) : [...current, permission]);
  const saveRights = async (event) => {
    event.preventDefault();
    if (!selectedUser) return;
    setSavingRights(true); setError(''); setNotice('');
    try {
      const updated = await updateUser({ id: selectedUser.id, role: editedRole, quotePermissions: permissions });
      setUsers((current) => current.map((user) => user.id === updated.id ? { ...user, ...updated } : user));
      setNotice(`Les droits de ${updated.firstName} ${updated.lastName} ont été enregistrés.`);
    } catch (requestError) { setError(requestError.message || 'La modification des droits a échoué.'); }
    finally { setSavingRights(false); }
  };
  const change = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault(); setSaving(true); setError(''); setNotice('');
    try {
      const user = await createUser(form);
      setUsers((current) => [...current, user].sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'fr')));
      setNotice(`${user.firstName} ${user.lastName} a été créé. Dossier cloud : ${user.collaboratorFolder}.`);
      setForm(initialForm);
    } catch (requestError) { setError(requestError.message || 'La création de l’utilisateur a échoué.'); }
    finally { setSaving(false); }
  };

  const administratorSelected = editedRole === 'admin';
  return <Box className="page-container module-page user-settings-page">
    <header className="user-settings-page__header">
      <div><Typography variant="h4">Gestion des utilisateurs</Typography><p>Créez les comptes et définissez leurs droits.</p></div>
      <Link className="basic-button" to="/administratif">Retour à l’administratif</Link>
    </header>
    {error && <Alert severity="error">{error}</Alert>}
    {notice && <Alert severity="success">{notice}</Alert>}

    <section className="user-settings-form" aria-labelledby="user-rights-title">
      <Typography id="user-rights-title" variant="h5">Modifier les droits</Typography>
      <p>Sélectionnez un utilisateur, puis ajustez son rôle et ses autorisations sur les devis.</p>
      <form onSubmit={saveRights}>
        <div className="user-settings-form__grid user-settings-form__selection">
          <Select fullWidth name="selectedUser" label="Utilisateur" value={selectedUserId} onChange={selectUser} disabled={loadingUsers} options={[{ value: '', label: loadingUsers ? 'Chargement…' : 'Sélectionner un utilisateur' }, ...users.map((user) => ({ value: String(user.id), label: `${user.firstName} ${user.lastName} — ${user.email}` }))]} />
          <Select fullWidth name="editedRole" label="Rôle" value={editedRole} onChange={changeRole} disabled={!selectedUserId} options={ROLE_OPTIONS} />
        </div>
        {selectedUser && <>
          {administratorSelected && <p className="user-settings-form__hint">Un administrateur dispose toujours de tous les droits.</p>}
          <div className="user-permissions">
            {QUOTE_PERMISSION_GROUPS.map((group) => <fieldset key={group.title} disabled={administratorSelected}>
              <legend>{group.title}</legend>
              {group.permissions.map(([value, label]) => <label key={value} className="user-permissions__item"><input type="checkbox" checked={administratorSelected || permissions.includes(value)} onChange={() => togglePermission(value)} /><span>{label}</span></label>)}
            </fieldset>)}
          </div>
          <Button type="submit" variant="primary" loading={savingRights}>Enregistrer les droits</Button>
        </>}
      </form>
    </section>

    <form className="user-settings-form" onSubmit={submit}>
      <Typography variant="h5">Créer un utilisateur</Typography>
      <div className="user-settings-form__grid">
        <Input fullWidth required name="firstName" label="Prénom" value={form.firstName} onChange={change} autoComplete="given-name" />
        <Input fullWidth required name="lastName" label="Nom" value={form.lastName} onChange={change} autoComplete="family-name" />
        <Input fullWidth required name="email" type="email" label="Adresse e-mail" value={form.email} onChange={change} autoComplete="email" />
        <Input fullWidth name="phone" type="tel" label="Téléphone" value={form.phone} onChange={change} autoComplete="tel" />
        <Input fullWidth required name="password" type="password" label="Mot de passe temporaire" value={form.password} onChange={change} minLength={8} helperText="8 caractères minimum" autoComplete="new-password" />
        <Select fullWidth name="role" label="Rôle" value={form.role} onChange={change} options={ROLE_OPTIONS} />
      </div>
      <p className="user-settings-form__cloud">Le dossier <strong>/RH/Collaborateurs/Prénom_Nom</strong> sera créé automatiquement.</p>
      <Button type="submit" variant="primary" loading={saving}>Créer l’utilisateur</Button>
    </form>
  </Box>;
}
